import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext'; // USE NEW AUTH CONTEXT

// Extend the Window interface to include the google property
declare global {
  interface Window {
    google?: any;
  }
}

interface LoginProps {
  open?: boolean; // Show on demand
  onClose?: () => void; // To close modal from parent
}


// Interface for the UserMaster entity fields for registration
interface UserMaster {
  userName?: string;
  password?: string;
  emailId?: string;
  mobileNo?: string;
  address?: string;
}

// Interface for the backend LoginResponse - UPDATED to include userName
interface LoginResponse {
  success: boolean;
  message: string;
  userRole?: number; // 0 for user, 1 for admin
  userName?: string; // ADDED: User's name from the backend
  suggestRegistration?: boolean; // Flag from backend to suggest registration
  requiresPassword?: boolean; // NEW: Flag from backend to indicate password is required
}


const Login: React.FC<LoginProps> = ({ open, onClose }) => {
  const [showModal, setShowModal] = useState(open || false);
  // const [shownOnce, setShownOnce] = useState(false); // REMOVED: No longer needed as we removed scroll logic

   const navigate = useNavigate();
    // const { setUser } = useAdmin(); // OLD: REMOVE THIS
    const { login } = useAuth(); // NEW: Use login from AuthContext
  
    const [identifier, setIdentifier] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [showRegistrationForm, setShowRegistrationForm] = useState<boolean>(false);
    const [showPasswordField, setShowPasswordField] = useState<boolean>(false);
    const [registrationData, setRegistrationData] = useState<UserMaster>({
      userName: '',
      password: '',
      emailId: '',
      mobileNo: '',
      address: '',
    });
    const [message, setMessage] = useState<string>('');
    const [isError, setIsError] = useState<boolean>(false);
  
    // NEW: State for Forgot Password functionality
    const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);
    const [otpSent, setOtpSent] = useState<boolean>(false);
    const [otp, setOtp] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');


     // --- Google Sign-in (optional, keep if still integrating) ---
      useEffect(() => {
        if (window.google) {
          window.google.accounts.id.initialize({
            client_id: 'YOUR_GOOGLE_CLIENT_ID', // Replace with your real client ID
            callback: handleGoogleCallback,
          });
          const googleDiv = document.getElementById('googleSignInDiv');
          if (googleDiv) {
            window.google.accounts.id.renderButton(
              googleDiv,
              { theme: 'outline', size: 'large', width: '100%' }
            );
          }
        }
      }, []);
    
      const handleGoogleCallback = (response: any) => {
        console.log('Google login success', response);
        setMessage('Google login successful! (Backend integration pending)');
        setIsError(false);
        // TODO: Integrate Google login with your backend to get userRole and userName
        // If successful, call login(userName, userRole) and redirect.
        handleClose(); // Close modal after successful Google login
      };
      // --- End Google Sign-in ---
    
      const handleIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIdentifier(e.target.value);
        setMessage('');
        setIsError(false);
        setShowRegistrationForm(false);
        setShowPasswordField(false);
        setShowForgotPassword(false); // Reset forgot password state
        setOtpSent(false); // Reset OTP state
        setOtp(''); // Clear OTP
        setNewPassword(''); // Clear new password
        setPassword('');
      };
    
      const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        setMessage('');
        setIsError(false);
      };
    
      const handleRegistrationInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setRegistrationData(prevData => ({ ...prevData, [name]: value }));
        setMessage('');
        setIsError(false);
      };
    
      // Step 1: Handle "Next" button click (initial identifier submission)
      const handleNextClick = async () => {
        setMessage('');
        setIsError(false);
    
        if (!identifier) {
          setMessage('Please enter your mobile number or email.');
          setIsError(true);
          return;
        }
    
        try {
          // const response = await fetch('http://localhost:8080/api/auth/login-or-register-check', {
          const response = await fetch('https://sardar-jewellers.com/api/auth/login-or-register-check', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ identifier }),
          });
    
          const data: LoginResponse = await response.json();
    
          if (response.ok) {
            if (data.suggestRegistration) {
              setMessage(data.message);
              setIsError(false);
              setShowRegistrationForm(true);
              setRegistrationData(prevData => ({
                ...prevData,
                emailId: identifier.includes('@') ? identifier : '',
                mobileNo: !identifier.includes('@') ? identifier : '',
              }));
            } else if (data.requiresPassword) {
              setMessage(data.message || 'User found. Please enter your password.');
              setIsError(false);
              setShowPasswordField(true);
            }
          } else {
            setMessage(data.message || 'An unexpected error occurred during check.');
            setIsError(true);
          }
        } catch (error) {
          console.error('Network or server error during identifier check:', error);
          setMessage('Could not connect to the server. Please try again later.');
          setIsError(true);
        }
      };
    
      // Step 2a: Handle Login (when password field is shown)
      const handleLoginSubmit = async () => {
        setMessage('');
        setIsError(false);
    
        if (!identifier || !password) {
          setMessage('Please enter both identifier and password.');
          setIsError(true);
          return;
        }
    
        try {
          // const response = await fetch('http://localhost:8080/api/auth/login', {
          const response = await fetch('https://sardar-jewellers.com/api/auth/login', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ identifier, password }),
          });
    
          const data: LoginResponse = await response.json();
    
          if (response.ok && data.success && data.userRole !== undefined) {
            setMessage(data.message);
            setIsError(false);
            // Store user data in context and local storage
            login(data.userName || 'User', data.userRole); // Use the login function from AuthContext
            localStorage.setItem('userToken', 'your-auth-token-if-any'); // Store actual token from backend if applicable
    
            redirectUser(data.userRole);
            handleClose(); // Close modal after successful login
          } else {
            setMessage(data.message || 'Login failed. Please check your credentials.');
            setIsError(true);
          }
        } catch (error) {
          console.error('Network or server error during login:', error);
          setMessage('Could not connect to the server. Please try again later.');
          setIsError(true);
        }
      };
    
      // Step 2b: Handle Registration (when registration form is shown)
      const handleRegisterSubmit = async () => {
        setMessage('');
        setIsError(false);
    
        if (!registrationData.userName || !registrationData.password || (!registrationData.emailId && !registrationData.mobileNo)) {
          setMessage('Please fill in all required fields (Name, Password, Email or Mobile).');
          setIsError(true);
          return;
        }
        if (registrationData.emailId && !registrationData.emailId.includes('@')) {
          setMessage('Please enter a valid email address.');
          setIsError(true);
          return;
        }
        if (registrationData.mobileNo && !/^\d{10}$/.test(registrationData.mobileNo)) {
          setMessage('Please enter a valid 10-digit mobile number.');
          setIsError(true);
          return;
        }
    
        try {
          // const response = await fetch('http://localhost:8080/api/auth/register', {
          const response = await fetch('https://sardar-jewellers.com/api/auth/register', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(registrationData),
          });
    
          if (response.ok) {
            setMessage('Registration successful! Please login with your new account.');
            setIsError(false);
            setIdentifier(registrationData.emailId || registrationData.mobileNo || '');
            setPassword('');
            setShowRegistrationForm(false);
            setShowPasswordField(true); // After successful registration, prompt for login
          } else {
            const errorData = await response.json();
            setMessage(errorData.message || 'Registration failed. User might already exist with this email/mobile.');
            setIsError(true);
          }
        } catch (error) {
          console.error('Network or server error during registration:', error);
          setMessage('Could not connect to the server. Please try again later.');
          setIsError(true);
        }
      };
    
      // NEW: Functions for Forgot Password
      const handleSendOtp = async () => {
        setMessage('');
        setIsError(false);
    
        if (!identifier) {
          setMessage('Please enter your email or mobile number to send OTP.');
          setIsError(true);
          return;
        }
    
        try {
          // Assuming 'identifier' is the email for forgot password
          // const res = await fetch(`http://localhost:8080/api/auth/forgot-password?email=${identifier}`, {
          const res = await fetch(`https://sardar-jewellers.com/api/auth/forgot-password?email=${identifier}`, {
            method: 'POST',
          });
          const msg = await res.text();
          if (res.ok) {
            setOtpSent(true);
            setMessage("OTP sent to your registered email.");
            setIsError(false);
          } else {
            setMessage(msg || "Failed to send OTP. Please check your email/mobile.");
            setIsError(true);
          }
        } catch (err) {
          console.error('Network or server error during OTP send:', err);
          setMessage("Could not connect to the server to send OTP. Please try again later.");
          setIsError(true);
        }
      };
    
      const handleResetPassword = async () => {
        setMessage('');
        setIsError(false);
    
        if (!otp || !newPassword) {
          setMessage('Please enter both OTP and New Password.');
          setIsError(true);
          return;
        }
        if (newPassword.length < 6) { // Example: minimum password length
            setMessage('New password must be at least 6 characters long.');
            setIsError(true);
            return;
        }
    
        try {
          // Verify OTP first
          // const verifyRes = await fetch(`http://localhost:8080/api/auth/verify-otp?email=${identifier}&otp=${otp}`, {
          const verifyRes = await fetch(`https://sardar-jewellers.com/api/auth/verify-otp?email=${identifier}&otp=${otp}`, {
            method: 'POST',
          });
          if (!verifyRes.ok) {
            const msg = await verifyRes.text();
            setMessage(msg || "Invalid OTP.");
            setIsError(true);
            return;
          }
    
          // If OTP is valid, proceed to reset password
          // const resetRes = await fetch(`http://localhost:8080/api/auth/reset-password?email=${identifier}&newPassword=${newPassword}`, {
          const resetRes = await fetch(`https://sardar-jewellers.com/api/auth/reset-password?email=${identifier}&newPassword=${newPassword}`, {
            method: 'POST',
          });
          const msg = await resetRes.text();
          if (resetRes.ok) {
            setMessage("Password updated successfully! Please login with your new password.");
            setIsError(false);
            // Reset states to go back to initial login screen or password input
            setShowForgotPassword(false);
            setOtpSent(false);
            setOtp('');
            setNewPassword('');
            setPassword(''); // Clear password field for new login
            setShowPasswordField(true); // Show password field for immediate login
          } else {
            setMessage(msg || "Password reset failed.");
            setIsError(true);
          }
        } catch (err) {
          console.error('Network or server error during password reset:', err);
          setMessage("Could not connect to the server to reset password. Please try again later.");
          setIsError(true);
        }
      };
    
    
      // Function to redirect based on user role
      const redirectUser = (role: number) => {
        if (role === 1) { // Admin role
          navigate('/admin/dashboard');
        } else if (role === 0) { // Regular user role
          navigate('/'); // Redirect to main website home
        } else {
          navigate('/'); // Fallback redirection
        }
      };
    

  // Open manually if parent sets open=true
  useEffect(() => {
    if (open) setShowModal(true);
  }, [open]);

  // Google Sign-in button rendering
  useEffect(() => {
    if (window.google && showModal) {
      window.google.accounts.id.initialize({
        client_id: 'YOUR_GOOGLE_CLIENT_ID', // Replace with your real client ID
        callback: handleGoogleCallback,
      });
      const googleDiv = document.getElementById('googleSignInDiv');
      if (googleDiv) {
        window.google.accounts.id.renderButton(
          googleDiv,
          { theme: 'outline', size: 'large', width: '100%' }
        );
      }
    }
  }, [showModal]);

  const handleClose = () => {
    setShowModal(false);
    onClose?.();
  };

  if (!showModal) return null;

  return (
    // Backdrop click: Close modal when clicking outside
    <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
        onClick={handleClose} 
    >
      {/* Modal Content - Prevent closing when clicking inside */}
      <div 
        className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md border-t-4 border-yellow-500 relative" 
        onClick={(e) => e.stopPropagation()} 
      >
        
        {/* Close Button/Icon */}
        <button 
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 transition duration-200 focus:outline-none"
          aria-label="Close"
        >
          {/* Simple 'X' icon */}
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>

        <h2 className="text-3xl font-bold mb-6 text-center text-gray-800 tracking-wide">Login / Register</h2>

        {/* Display message/error */}
        {message && (
          <div className={`mb-4 p-3 rounded text-center font-medium ${isError ? 'bg-red-100 text-red-700 border border-red-300' : 'bg-green-100 text-green-700 border border-green-300'}`}>
            {message}
          </div>
        )}

        {/* --- Conditional Form Rendering --- */}

        {/* Initial Identifier Input */}
        {!showRegistrationForm && !showPasswordField && (
          <>
            <input
              type="text"
              placeholder="Mobile No. or Email"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition duration-200"
              value={identifier}
              onChange={handleIdentifierChange}
            />
            <button
              onClick={handleNextClick}
              className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-bold py-3 rounded-lg hover:from-yellow-600 hover:to-amber-600 transition duration-300 shadow-lg transform hover:scale-105"
            >
              Next
            </button>
          </>
        )}

        {/* Password Input (if user exists after initial check) AND Forgot Password Flow */}
        {showPasswordField && !showRegistrationForm && (
          <>
            <p className="text-center text-gray-600 mb-4 text-lg">
              Please enter your password for <span className="font-semibold text-gray-800">{identifier}</span>
            </p>

            {!showForgotPassword && (
              <>
                <input
                  type="password"
                  placeholder="Password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition duration-200"
                  value={password}
                  onChange={handlePasswordChange}
                />
                <button
                  onClick={handleLoginSubmit}
                  className="w-full bg-gradient-to-r from-yellow-600 to-amber-700 text-white font-bold py-3 rounded-lg hover:from-yellow-700 hover:to-amber-800 transition duration-300 shadow-lg transform hover:scale-105"
                >
                  Login
                </button>
                <p
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-blue-600 mt-3 text-center cursor-pointer hover:underline"
                >
                  Forgot Password?
                </p>
              </>
            )}

            {showForgotPassword && !otpSent && (
              <>
                <button
                  onClick={handleSendOtp} // Use the new handler
                  className="w-full bg-yellow-600 text-white font-bold py-3 rounded-lg mt-2 hover:bg-yellow-700 transition duration-300"
                >
                  Send OTP to Email
                </button>
              </>
            )}

            {showForgotPassword && otpSent && (
              <>
                <input
                  type="text"
                  placeholder="Enter OTP"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-yellow-500"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                />
                <input
                  type="password"
                  placeholder="New Password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-yellow-500"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <button
                  onClick={handleResetPassword} // Use the new handler
                  className="w-full bg-green-600 text-white font-bold py-3 rounded-lg mt-2 hover:bg-green-700 transition duration-300"
                >
                  Reset Password
                </button>
              </>
            )}

            <button
              onClick={() => {
                setShowPasswordField(false);
                setShowForgotPassword(false); // Reset forgot password state
                setOtpSent(false); // Reset OTP state
                setOtp(''); // Clear OTP
                setNewPassword(''); // Clear new password
                setIdentifier('');
                setPassword('');
                setMessage('');
                setIsError(false);
              }}
              className="w-full bg-gray-400 text-white font-bold py-3 rounded-lg hover:bg-gray-500 transition duration-300 shadow-md mt-4 transform hover:scale-105"
            >
              Back
            </button>
          </>
        )}

        {/* Registration Form (if user does not exist after initial check) */}
        {showRegistrationForm && (
          <>
            <p className="text-center text-gray-600 mb-4 text-lg">No account found. Please register.</p>
            <input
              type="text"
              name="userName"
              placeholder="Full Name (Required)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition duration-200"
              value={registrationData.userName}
              onChange={handleRegistrationInputChange}
            />
            <input
              type="password"
              name="password"
              placeholder="Create Password (Required)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition duration-200"
              value={registrationData.password}
              onChange={handleRegistrationInputChange}
            />
            <input
              type="email"
              name="emailId"
              placeholder="Email ID (Optional, but required if mobile is empty)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition duration-200"
              value={registrationData.emailId}
              onChange={handleRegistrationInputChange}
            />
            <input
              type="tel"
              name="mobileNo"
              placeholder="Mobile No. (Optional, but required if email is empty)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition duration-200"
              value={registrationData.mobileNo}
              onChange={handleRegistrationInputChange}
            />
            <input
              type="text"
              name="address"
              placeholder="Address (Optional)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition duration-200"
              value={registrationData.address}
              onChange={handleRegistrationInputChange}
            />
            <button
              onClick={handleRegisterSubmit}
              className="w-full bg-gradient-to-r from-yellow-600 to-orange-700 text-white font-bold py-3 rounded-lg hover:from-yellow-700 hover:to-orange-800 transition duration-300 shadow-lg transform hover:scale-105"
            >
              Register
            </button>
            <button
              onClick={() => {
                setShowRegistrationForm(false);
                setIdentifier('');
                setRegistrationData({});
                setMessage('');
                setIsError(false);
              }}
              className="w-full bg-gray-400 text-white font-bold py-3 rounded-lg hover:bg-gray-500 transition duration-300 shadow-md mt-2 transform hover:scale-105"
            >
              Back to Initial Check
            </button>
          </>
        )}

        {/* Separator for Google Login */}
        {/* <div className="text-center text-sm text-gray-500 my-6">or</div> */}

        {/* Google Login Button */}
        {/* <div id="googleSignInDiv" className="w-full flex justify-center" /> */}

        {/* Newsletter checkbox (if desired) */}
        {/* <label className="flex items-center mt-4 text-sm text-gray-700">
          <input type="checkbox" className="mr-2" />
          Keep up with our latest news and events. Subscribe to our newsletter.
        </label> */}
      </div>
    </div>
  );
};

export default Login;